import { Carousel } from 'react-bootstrap'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { AdvertType, CarouselImage } from '../../types/baseTypes'
import { StyledHomeSlideShow, StyledVideo } from './SlideShow.styled'
import { Loader } from 'components/Loader'
import { BaseVehicleSearchCriteriaColumn, SessionStorageKey } from 'common/constants'
import { ISearchFilterResult } from 'types/vehicleTypes'
import { useFetch } from 'hooks/useFetch'
import { addDummyChildrenFilter, selectChildrenFilter } from 'common/helpers'
import { saveAs } from 'file-saver'
import { useSrpFiltersStore } from 'store/useSrpFiltersStore'
import { getCarouselImages } from 'apis/dashboardApis'
import { shallow } from 'zustand/shallow'
import { isAnyChildrenFilterExist } from '../../common/helpers'
import { useHistory } from 'react-router-dom'
import { AppRoutes } from 'common/routes'
import { resetSRPFilter } from 'utils/menuUtils'
import { useEffect, useState, useRef } from 'react'
import ReactPlayer from 'react-player/lazy'
import { SlideShowPromotion } from './SlideShowPromotion'

export const SlideShow = () => {
  const { filters } = useSrpFiltersStore((state) => state, shallow)
  const { loading, data } = useFetch(() => getCarouselImages())
  const history = useHistory()
  const [interval, setInterval] = useState(3000)
  const [activeIndex, setActiveIndex] = useState(0)
  const playerRefs = useRef<(ReactPlayer | null)[]>([])
  const [carouselItems, setCarouselItems] = useState<CarouselImage[]>([])

  const handleRedirect = (ft?: ISearchFilterResult[]) => {
    resetSRPFilter()
    sessionStorage.setItem(SessionStorageKey.SEARCH_FILTERS, ft ? JSON.stringify(ft) : null)
    history.push(AppRoutes.SearchResult)
  }

  const clickHandler = (e: MouseEvent, item: CarouselImage, index: number) => {
    e.preventDefault()
    if (item.AdvertType === AdvertType.NonPromotional) return
    if (item.AdvertType === AdvertType.Promotional) {
      if (isAnyChildrenFilterExist(filters, 'Promotion', item.Promotion)) {
        selectChildrenFilter(filters, 'Promotion', item.Promotion)
      } else {
        addDummyChildrenFilter(
          filters,
          BaseVehicleSearchCriteriaColumn.Promotion,
          'Promotion',
          'promotion',
          item.Promotion
        )
      }
      handleRedirect(filters)
    } else if (item.AdvertType === AdvertType.WebLink) {
      if (item.DocumentURL) {
        window.open(item.DocumentURL, '_blank', 'noopener,noreferrer')
      }
    } else {
      if (item.DocumentURL) saveAs(item.DocumentURL, item.DocumentName)
    }
  }

  const handleDuration = (duration: number, eventIndex: number) => {
    setInterval(duration * 1000)
    setCarouselItems((prev) =>
      prev.map((item, index) => {
        if (index === eventIndex) {
          return { ...item, Duration: duration }
        }
        return item
      })
    )
  }

  const handleSlide = (eventKey: number) => {
    setActiveIndex(eventKey)
    if (carouselItems[eventKey].IsVideo) {
      if (playerRefs.current[eventKey]) {
        playerRefs.current[eventKey]?.seekTo(0)
      }
      setInterval(carouselItems[eventKey].Duration * 1000)
    } else {
      setInterval(3000)
    }
  }

  const handleVideoEnded = (index: number) => {
    // Exit fullscreen if it's active
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error('Error exiting fullscreen:', err)
      })
    }

    // Wait a small delay to ensure fullscreen is exited before changing slides
    setTimeout(() => {
      if (index < carouselItems.length - 1) setActiveIndex(index + 1)
      else setActiveIndex(0)
    }, 100)
  }

  useEffect(() => {
    data?.forEach((item) => {
      setCarouselItems((prev) => [...prev, item])
    })
  }, [data])

  return (
    <StyledHomeSlideShow>
      <Carousel
        interval={interval}
        fade={true}
        controls={false}
        indicators={carouselItems?.length > 1}
        activeIndex={activeIndex}
        onSelect={setActiveIndex}
        onSlide={handleSlide}
      >
        {!loading ? (
          carouselItems?.map((item, index) => {
            return item.IsVideo ? (
              <Carousel.Item key={index}>
                <StyledVideo>
                  <ReactPlayer
                    ref={(el) => (playerRefs.current[index] = el)}
                    url={item.ImageURL}
                    controls={true}
                    muted={true}
                    width="100%"
                    height="100%"
                    playing={index === activeIndex}
                    onDuration={(duration) => handleDuration(duration, index)}
                    onEnded={() => handleVideoEnded(index)}
                    onContextMenu={(e) => e.preventDefault()}
                    config={{
                      file: {
                        attributes: {
                          controlsList: 'nodownload noplaybackrate',
                          disablePictureInPicture: true
                        }
                      }
                    }}
                  />
                </StyledVideo>
                <SlideShowPromotion item={item} index={index} clickHandler={clickHandler} />
              </Carousel.Item>
            ) : (
              <Carousel.Item key={index}>
                <LazyLoadImage
                  placeholder={<img alt=""></img>}
                  onClick={(e) => clickHandler(e, item, index)}
                  className={`d-block w-100 h-100 ${item.AdvertType !== AdvertType.NonPromotional && 'clickable'}`}
                  src={item.ImageURL}
                />
                <SlideShowPromotion item={item} index={index} clickHandler={clickHandler} />
              </Carousel.Item>
            )
          })
        ) : (
          <Carousel.Item>
            <img className="d-block w-100" alt={''} />
            <Loader overlay />
          </Carousel.Item>
        )}
      </Carousel>
    </StyledHomeSlideShow>
  )
}
